/**
 * AI Video Library Page
 * View, play, and manage AI-generated training videos
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Video,
  Plus,
  Search,
  Play,
  Pause,
  Trash2,
  Download,
  Copy,
  MoreVertical,
  Loader2,
  Sparkles,
  Clock,
  Calendar,
  Film,
  Maximize,
  Volume2,
  VolumeX,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Layers,
  GripVertical,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATION_OPTIONS = [
  { value: 4, label: '4 seconds', description: 'Quick intro/outro' },
  { value: 8, label: '8 seconds', description: 'Short demonstration' },
  { value: 12, label: '12 seconds', description: 'Detailed explanation (Max per scene)' },
];

const SIZE_OPTIONS = [
  { value: '1280x720', label: '720p (16:9)', description: 'Standard HD' },
  { value: '1024x1024', label: '1:1 Square', description: 'Social media' },
  { value: '1792x1024', label: 'Wide (16:9)', description: 'Cinematic' },
  { value: '1024x1792', label: 'Portrait (9:16)', description: 'Mobile vertical' },
];

export const AIVideoLibrary = () => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Generate dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationMode, setGenerationMode] = useState('single'); // 'single' or 'multi'
  const [generateForm, setGenerateForm] = useState({
    title: '',
    prompt: '',
    duration: 8,
    size: '1280x720',
    model: 'sora-2',
  });
  
  // Multi-scene state
  const [scenes, setScenes] = useState([
    { prompt: '', duration: 8 },
  ]);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  
  // Video player dialog
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchVideos = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ai/videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data || []);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
      toast.error(t('aiVideo.fetchFailed', 'Failed to load videos'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Scene management functions
  const addScene = () => {
    if (scenes.length < 15) {
      setScenes([...scenes, { prompt: '', duration: 8 }]);
    }
  };

  const removeScene = (index) => {
    if (scenes.length > 1) {
      setScenes(scenes.filter((_, i) => i !== index));
    }
  };

  const updateScene = (index, field, value) => {
    const newScenes = [...scenes];
    newScenes[index] = { ...newScenes[index], [field]: value };
    setScenes(newScenes);
  };

  const getTotalDuration = () => {
    return scenes.reduce((total, scene) => total + scene.duration, 0);
  };

  const resetForm = () => {
    setGenerateForm({
      title: '',
      prompt: '',
      duration: 8,
      size: '1280x720',
      model: 'sora-2',
    });
    setScenes([{ prompt: '', duration: 8 }]);
    setGenerationMode('single');
    setGenerationProgress({ current: 0, total: 0 });
  };

  const handleGenerate = async () => {
    if (generationMode === 'single') {
      // Single video generation
      if (!generateForm.title || !generateForm.prompt) {
        toast.error(t('aiVideo.fillRequired', 'Please fill in title and prompt'));
        return;
      }

      setGenerating(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API_URL}/ai/generate-video`, generateForm, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 600000,
        });

        if (response.data.success) {
          toast.success(t('aiVideo.generated', 'Video generated successfully!'));
          setGenerateDialogOpen(false);
          resetForm();
          fetchVideos();
        }
      } catch (error) {
        console.error('Video generation failed:', error);
        toast.error(error.response?.data?.detail || t('aiVideo.generateFailed', 'Failed to generate video'));
      } finally {
        setGenerating(false);
      }
    } else {
      // Multi-scene video generation
      if (!generateForm.title) {
        toast.error(t('aiVideo.titleRequired', 'Please enter a video title'));
        return;
      }

      const validScenes = scenes.filter(s => s.prompt.trim());
      if (validScenes.length === 0) {
        toast.error(t('aiVideo.addScenes', 'Please add at least one scene with a prompt'));
        return;
      }

      setGenerating(true);
      setGenerationProgress({ current: 0, total: validScenes.length });

      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API_URL}/ai/generate-multi-scene-video`, {
          title: generateForm.title,
          scenes: validScenes,
          size: generateForm.size,
          model: generateForm.model,
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 1800000, // 30 minute timeout for multi-scene
        });

        if (response.data.success) {
          toast.success(
            t('aiVideo.multiSceneGenerated', 
              `Video generated! ${response.data.scene_count} scenes, ${response.data.total_duration}s total`
            )
          );
          setGenerateDialogOpen(false);
          resetForm();
          fetchVideos();
        }
      } catch (error) {
        console.error('Multi-scene video generation failed:', error);
        toast.error(error.response?.data?.detail || t('aiVideo.generateFailed', 'Failed to generate video'));
      } finally {
        setGenerating(false);
        setGenerationProgress({ current: 0, total: 0 });
      }
    }
  };

  const handlePlayVideo = (video) => {
    setSelectedVideo(video);
    setPlayerDialogOpen(true);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleCopyUrl = (video) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${video.url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success(t('aiVideo.urlCopied', 'Video URL copied to clipboard'));
  };

  const handleDownload = (video) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${video.url}`;
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = video.filename || 'ai-video.mp4';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteVideo = async () => {
    if (!videoToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/ai/videos/${videoToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('aiVideo.deleted', 'Video deleted successfully'));
      setDeleteDialogOpen(false);
      setVideoToDelete(null);
      fetchVideos();
    } catch (error) {
      toast.error(t('aiVideo.deleteFailed', 'Failed to delete video'));
    } finally {
      setDeleting(false);
    }
  };

  const filteredVideos = videos.filter(video =>
    video.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.prompt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDuration = (seconds) => {
    return `${seconds}s`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Film className="w-7 h-7 text-primary" />
              {t('aiVideo.title', 'AI Video Library')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('aiVideo.description', 'Generate, manage, and use AI-created training videos')}
            </p>
          </div>
          <Button onClick={() => setGenerateDialogOpen(true)} data-testid="generate-video-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            {t('aiVideo.generateNew', 'Generate New Video')}
          </Button>
        </div>

        {/* Search & Stats */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('aiVideo.searchPlaceholder', 'Search videos by title or prompt...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-videos"
            />
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Video className="w-4 h-4" />
              {videos.length} {t('aiVideo.totalVideos', 'videos')}
            </span>
            <Button variant="outline" size="sm" onClick={fetchVideos}>
              <RefreshCw className="w-4 h-4 mr-1" />
              {t('common.refresh', 'Refresh')}
            </Button>
          </div>
        </div>

        {/* Video Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredVideos.map((video) => (
              <Card 
                key={video.id} 
                className="group overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`video-card-${video.id}`}
              >
                {/* Video Thumbnail / Preview */}
                <div 
                  className="relative aspect-video bg-gradient-to-br from-primary/20 to-primary/5 cursor-pointer"
                  onClick={() => handlePlayVideo(video)}
                >
                  <video
                    src={`${process.env.REACT_APP_BACKEND_URL}${video.url}`}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-6 h-6 text-primary ml-1" />
                    </div>
                  </div>
                  {/* Duration badge */}
                  <Badge className="absolute bottom-2 right-2 bg-black/70">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatDuration(video.duration)}
                  </Badge>
                  {/* AI badge */}
                  <Badge className="absolute top-2 left-2 bg-primary/90">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI
                  </Badge>
                  {/* Multi-scene badge */}
                  {video.is_multi_scene && (
                    <Badge className="absolute top-2 right-2 bg-purple-500/90">
                      <Layers className="w-3 h-3 mr-1" />
                      {video.scene_count} scenes
                    </Badge>
                  )}
                </div>

                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{video.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {video.prompt}
                      </p>
                    </div>
                    
                    {/* Actions dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handlePlayVideo(video)}>
                          <Play className="w-4 h-4 mr-2" />
                          {t('aiVideo.play', 'Play')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCopyUrl(video)}>
                          <Copy className="w-4 h-4 mr-2" />
                          {t('aiVideo.copyUrl', 'Copy URL')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload(video)}>
                          <Download className="w-4 h-4 mr-2" />
                          {t('aiVideo.download', 'Download')}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => {
                            setVideoToDelete(video);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {t('common.delete', 'Delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Meta info */}
                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {video.created_at ? format(new Date(video.created_at), 'MMM d, yyyy') : 'N/A'}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {video.size || '1280x720'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Film className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">
              {searchQuery 
                ? t('aiVideo.noResults', 'No videos found')
                : t('aiVideo.noVideos', 'No AI videos yet')}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery 
                ? t('aiVideo.tryDifferentSearch', 'Try a different search term')
                : t('aiVideo.generateFirst', 'Generate your first AI training video')}
            </p>
            {!searchQuery && (
              <Button onClick={() => setGenerateDialogOpen(true)}>
                <Sparkles className="w-4 h-4 mr-2" />
                {t('aiVideo.generateNew', 'Generate New Video')}
              </Button>
            )}
          </Card>
        )}

        {/* Generate Video Dialog */}
        <Dialog open={generateDialogOpen} onOpenChange={(open) => {
          setGenerateDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                {t('aiVideo.generateTitle', 'Generate AI Training Video')}
              </DialogTitle>
              <DialogDescription>
                {t('aiVideo.generateDescription', 'Create professional training videos using AI powered by Sora 2.')}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
              {/* Mode Toggle */}
              <div className="flex gap-2 p-1 bg-muted rounded-lg">
                <Button
                  variant={generationMode === 'single' ? 'default' : 'ghost'}
                  size="sm"
                  className="flex-1"
                  onClick={() => setGenerationMode('single')}
                  disabled={generating}
                >
                  <Video className="w-4 h-4 mr-2" />
                  {t('aiVideo.singleClip', 'Single Clip')}
                  <Badge variant="secondary" className="ml-2 text-xs">Max 12s</Badge>
                </Button>
                <Button
                  variant={generationMode === 'multi' ? 'default' : 'ghost'}
                  size="sm"
                  className="flex-1"
                  onClick={() => setGenerationMode('multi')}
                  disabled={generating}
                >
                  <Layers className="w-4 h-4 mr-2" />
                  {t('aiVideo.multiScene', 'Multi-Scene')}
                  <Badge variant="secondary" className="ml-2 text-xs">Up to 3 min</Badge>
                </Button>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">{t('aiVideo.videoTitle', 'Video Title')} *</Label>
                <Input
                  id="title"
                  value={generateForm.title}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={t('aiVideo.titlePlaceholder', 'e.g., Customer Greeting Demo')}
                  data-testid="video-title-input"
                  disabled={generating}
                />
              </div>

              {/* Single Mode: Single Prompt */}
              {generationMode === 'single' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="prompt">{t('aiVideo.prompt', 'Video Description/Prompt')} *</Label>
                    <Textarea
                      id="prompt"
                      value={generateForm.prompt}
                      onChange={(e) => setGenerateForm(prev => ({ ...prev, prompt: e.target.value }))}
                      placeholder={t('aiVideo.promptPlaceholder', 'Describe the video you want to create. Be specific about the scene, actions, and style...')}
                      rows={4}
                      data-testid="video-prompt-input"
                      disabled={generating}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('aiVideo.promptTip', 'Tip: Include details about setting, characters, actions, and visual style.')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('aiVideo.duration', 'Duration')}</Label>
                      <Select
                        value={generateForm.duration.toString()}
                        onValueChange={(v) => setGenerateForm(prev => ({ ...prev, duration: parseInt(v) }))}
                        disabled={generating}
                      >
                        <SelectTrigger data-testid="video-duration-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DURATION_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value.toString()}>
                              <span className="font-medium">{opt.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('aiVideo.size', 'Resolution')}</Label>
                      <Select
                        value={generateForm.size}
                        onValueChange={(v) => setGenerateForm(prev => ({ ...prev, size: v }))}
                        disabled={generating}
                      >
                        <SelectTrigger data-testid="video-size-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SIZE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <span className="font-medium">{opt.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {/* Multi-Scene Mode */}
              {generationMode === 'multi' && (
                <>
                  {/* Resolution for all scenes */}
                  <div className="space-y-2">
                    <Label>{t('aiVideo.resolution', 'Resolution (All Scenes)')}</Label>
                    <Select
                      value={generateForm.size}
                      onValueChange={(v) => setGenerateForm(prev => ({ ...prev, size: v }))}
                      disabled={generating}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SIZE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span className="font-medium">{opt.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">- {opt.description}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Total duration indicator */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="font-medium">{t('aiVideo.totalDuration', 'Total Duration')}</span>
                    </div>
                    <Badge variant="default" className="text-lg px-3">
                      {getTotalDuration()}s ({Math.floor(getTotalDuration() / 60)}:{String(getTotalDuration() % 60).padStart(2, '0')})
                    </Badge>
                  </div>

                  {/* Scenes List */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>{t('aiVideo.scenes', 'Scenes')} ({scenes.length}/15)</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addScene}
                        disabled={scenes.length >= 15 || generating}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('aiVideo.addScene', 'Add Scene')}
                      </Button>
                    </div>

                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                      {scenes.map((scene, index) => (
                        <div 
                          key={index}
                          className="p-4 rounded-lg border bg-card space-y-3"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                {index + 1}
                              </div>
                              <span className="font-medium text-sm">
                                {t('aiVideo.scene', 'Scene')} {index + 1}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Select
                                value={scene.duration.toString()}
                                onValueChange={(v) => updateScene(index, 'duration', parseInt(v))}
                                disabled={generating}
                              >
                                <SelectTrigger className="w-24 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {DURATION_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value.toString()}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {scenes.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => removeScene(index)}
                                  disabled={generating}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <Textarea
                            value={scene.prompt}
                            onChange={(e) => updateScene(index, 'prompt', e.target.value)}
                            placeholder={t('aiVideo.scenePromptPlaceholder', 'Describe what happens in this scene...')}
                            rows={2}
                            disabled={generating}
                            className="text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Info box */}
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-700">{t('aiVideo.generationNote', 'Generation Time')}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      {generationMode === 'single'
                        ? t('aiVideo.singleTimeInfo', 'Single clip takes 1-3 minutes to generate.')
                        : t('aiVideo.multiTimeInfo', 'Multi-scene videos take 2-5 minutes per scene. All scenes are generated and merged automatically.')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Generation Progress */}
              {generating && generationMode === 'multi' && (
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('aiVideo.generatingScenes', 'Generating scenes...')}</span>
                    <span className="text-sm text-muted-foreground">
                      {t('aiVideo.pleaseWait', 'Please wait, do not close this dialog')}
                    </span>
                  </div>
                  <Progress value={(generationProgress.current / generationProgress.total) * 100} />
                </div>
              )}
            </div>

            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setGenerateDialogOpen(false)} disabled={generating}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleGenerate} disabled={generating} data-testid="generate-btn">
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('aiVideo.generating', 'Generating...')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {generationMode === 'single' 
                      ? t('aiVideo.generate', 'Generate Video')
                      : t('aiVideo.generateAndMerge', 'Generate & Merge')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Video Player Dialog */}
        <Dialog open={playerDialogOpen} onOpenChange={setPlayerDialogOpen}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden">
            <div className="relative bg-black">
              <video
                ref={videoRef}
                src={selectedVideo ? `${process.env.REACT_APP_BACKEND_URL}${selectedVideo.url}` : ''}
                className="w-full aspect-video"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                controls={false}
              />
              
              {/* Custom controls overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-white hover:bg-white/20"
                      onClick={togglePlay}
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-white hover:bg-white/20"
                      onClick={toggleMute}
                    >
                      {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </Button>
                    {selectedVideo && (
                      <span className="text-white text-sm ml-2">
                        {selectedVideo.title}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-white hover:bg-white/20"
                      onClick={() => selectedVideo && handleCopyUrl(selectedVideo)}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {t('aiVideo.copyUrl', 'Copy URL')}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-white hover:bg-white/20"
                      onClick={() => selectedVideo && handleDownload(selectedVideo)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      {t('aiVideo.download', 'Download')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Play button overlay when paused */}
              {!isPlaying && (
                <div 
                  className="absolute inset-0 flex items-center justify-center cursor-pointer"
                  onClick={togglePlay}
                >
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-colors">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </div>
                </div>
              )}
            </div>

            {/* Video details */}
            {selectedVideo && (
              <div className="p-4 space-y-3">
                <h3 className="font-semibold text-lg">{selectedVideo.title}</h3>
                <p className="text-sm text-muted-foreground">{selectedVideo.prompt}</p>
                <div className="flex items-center gap-4 text-sm">
                  <Badge variant="outline">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatDuration(selectedVideo.duration)}
                  </Badge>
                  <Badge variant="outline">
                    {selectedVideo.size || '1280x720'}
                  </Badge>
                  <span className="text-muted-foreground">
                    {selectedVideo.created_at && format(new Date(selectedVideo.created_at), 'PPP')}
                  </span>
                </div>
                
                {/* Use in course tip */}
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 mt-4">
                  <div className="flex items-start gap-2">
                    <ExternalLink className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-700">{t('aiVideo.useInCourse', 'Use in Course')}</p>
                      <p className="text-muted-foreground text-xs mt-1">
                        {t('aiVideo.useInCourseInfo', 'Copy the video URL and paste it when adding a video module to your course.')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('aiVideo.deleteTitle', 'Delete Video')}</DialogTitle>
              <DialogDescription>
                {t('aiVideo.deleteConfirm', 'Are you sure you want to delete this video? This action cannot be undone.')}
              </DialogDescription>
            </DialogHeader>
            {videoToDelete && (
              <div className="py-4">
                <p className="font-medium">{videoToDelete.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{videoToDelete.prompt}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button variant="destructive" onClick={handleDeleteVideo} disabled={deleting}>
                {deleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {t('common.delete', 'Delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AIVideoLibrary;
